import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUploadLearnSectionImageMutation } from '@/client/api/learn';

// The markdown image syntax uses `"` to close the caption and `)` / `]` to end
// the token, so strip them from what the editor types.
const clean = (s: string) => s.replace(/["\[\]()]/g, '').replace(/\s+/g, ' ').trim();

interface Props {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the finished `![alt](url "caption")` line. */
  onInsert: (markdownLine: string) => void;
}

export const InsertPhotoDialog: React.FC<Props> = ({ slug, open, onOpenChange, onInsert }) => {
  const upload = useUploadLearnSectionImageMutation();
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState('');
  const [caption, setCaption] = useState('');
  const [credit, setCredit] = useState('');
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const reset = () => {
    setFile(null);
    setAlt('');
    setCaption('');
    setCredit('');
  };

  const ready = Boolean(file && clean(alt) && clean(credit));

  const handleUpload = async () => {
    if (!file || !ready) return;
    try {
      const { url } = await upload.mutateAsync({ slug, file, credit: clean(credit) });
      const fullCaption = [clean(caption), clean(credit)].filter(Boolean).join(' ');
      onInsert(`![${clean(alt)}](${url} "${fullCaption}")`);
      toast.success('Photo uploaded and added to the markdown');
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload photo');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a photo</DialogTitle>
          <DialogDescription>
            Use real photos you have the right to publish: your own, or royalty-free with no watermark. The photo is
            added where your cursor is in the markdown.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="learn-photo-file">Photo *</Label>
            <Input
              id="learn-photo-file"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] ?? null)}
            />
            {previewUrl && (
              <img
                src={previewUrl}
                alt=""
                className="mt-2 max-h-48 rounded-md border border-gray-200 object-contain"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="learn-photo-alt">Description *</Label>
            <Input
              id="learn-photo-alt"
              value={alt}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAlt(e.target.value)}
              placeholder="e.g. A phone with a shattered screen on a table"
            />
            <p className="text-[11px] text-gray-500">What the photo shows, for screen readers and search.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="learn-photo-caption">Caption</Label>
            <Input
              id="learn-photo-caption"
              value={caption}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCaption(e.target.value)}
              placeholder="e.g. A shattered screen. If the phone still works, this is Used - Poor."
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="learn-photo-credit">Credit and licence *</Label>
            <Input
              id="learn-photo-credit"
              value={credit}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCredit(e.target.value)}
              placeholder="e.g. Photo: FeelyFeely.  or  Photo: Jane Doe, CC BY-SA 4.0, via Wikimedia Commons."
            />
            <p className="text-[11px] text-gray-500">Shown under the photo and stored with it.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!ready || upload.isPending} className="gap-1.5">
            {upload.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload and insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
